import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const themeStorageKey = 'rw-theme';
export type ColorTheme = 'light' | 'dark';

function isTheme(value: string | null | undefined): value is ColorTheme {
  return value === 'light' || value === 'dark';
}

export function readColorTheme(): ColorTheme {
  if (typeof document !== 'undefined') {
    const current = document.documentElement.getAttribute('data-theme');
    if (isTheme(current)) return current;
  }
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(themeStorageKey);
    if (isTheme(stored)) return stored;
  }
  if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function applyColorTheme(theme: ColorTheme) {
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.style.colorScheme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', theme === 'dark' ? '#131313' : '#ffffff');
  }
}

interface ThemeContextValue {
  theme: ColorTheme;
  setTheme: (theme: ColorTheme) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: PropsWithChildren) {
  const [theme, setThemeState] = useState<ColorTheme>(readColorTheme);
  useEffect(() => {
    applyColorTheme(theme);
  }, [theme]);
  const setTheme = useCallback((next: ColorTheme) => {
    localStorage.setItem(themeStorageKey, next);
    applyColorTheme(next);
    setThemeState(next);
  }, []);
  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
